import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';

import { mapProps } from '../../../../../../../util/hocs';
import IdentityTagSelection from './IdentityTagSelection';
import withModal from '../../../../../../../components/global/modal/withModal';
import IdentityTagSelectionHeader from '../IdentityTagSelectionHeader';
import { getIdentityName } from '../../../../../../../util/identity';
import Button from '../../../../../../../components/global/Button';
import { getAsset } from '../../../../../../../util/assets';
import getIdentityAlbumFolderTags from '../../graphql/queries/getIdentityAlbumFolderTags';
import { albumFolderRequiresTagsToOnboard } from '../../helpers';
import { AVATAR_SIZE_36 } from '../../../../../../../constants/avatar-sizes';
import IdentityAvatarContainer from '../../../../../../../containers/global/identity/IdentityAvatarContainer';
import Spinner from '../../../../../../../components/global/Spinner';
import useTagsForSportsCheckSkip from './hooks/useTagsForSportsCheckSkip';

export const IDENTITY_TAG_SELECTION_MODAL =
  'modals/IDENTITY_TAG_SELECTION_MODAL';

const propTypes = {
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onRequestClose: PropTypes.func.isRequired,
  actionProps: PropTypes.shape({
    onComplete: PropTypes.func,
  }),
  onDeselectIdentity: PropTypes.func.isRequired,
};

const IdentityTagSelectionModal = ({
  identity,
  albumFolder,
  onRequestClose,
  onDeselectIdentity,
  actionProps,
}) => {
  const { onComplete } = actionProps ?? {};
  const { loadingTags, shouldSkipSportsCheck } = useTagsForSportsCheckSkip({
    identity,
    albumFolderId: albumFolder?.id,
  });
  const [hasSport, setHasSport] = useState(false);
  const identityName = getIdentityName(identity);

  useEffect(() => {
    if (shouldSkipSportsCheck) {
      setHasSport(true);
    }
  }, [loadingTags]);

  if (loadingTags) {
    return (
      <div className="text-center p-6">
        <Spinner />
      </div>
    );
  }

  if (!hasSport) {
    // Ask the user if there is a sport to be added:
    return (
      <Fragment>
        <img
          src={getAsset('/sports-illustration.png')}
          alt=""
          className="identity-tag-selection-heading-image d-block mx-auto mt-4"
        />
        <h4 className=" text-center">
          Does{' '}
          <IdentityAvatarContainer
            size={AVATAR_SIZE_36}
            identity={identity}
            className="mb-1"
          />{' '}
          {identityName} play sports with numbered jerseys, bibs, or plates?
        </h4>
        <div className="identity-tag-selection-actions mt-3 mx-3">
          <Button
            btnBlock
            type="submit"
            onClick={() => {
              setHasSport(true);
            }}
          >
            Yes, proceed to select sports
          </Button>

          <Button
            btnStyle="link"
            className="text-center text-bold mb-4"
            btnBlock
            onClick={() => {
              if (albumFolderRequiresTagsToOnboard(albumFolder)) {
                onDeselectIdentity(identity);
                onRequestClose();
                return;
              }

              onRequestClose();
              onComplete?.();
            }}
          >
            {albumFolderRequiresTagsToOnboard(albumFolder)
              ? `Choose someone else.`
              : `No, ${identityName} is not on a sports team with numbered jerseys, bibs, or number plates.`}
          </Button>
        </div>
      </Fragment>
    );
  }

  return (
    <div className="identity-tag-selection-modal-container">
      <IdentityTagSelectionHeader
        identity={identity}
        albumFolder={albumFolder}
        className="mb-2"
        onRequestClose={
          albumFolderRequiresTagsToOnboard(albumFolder)
            ? undefined
            : onRequestClose
        }
      />

      <IdentityTagSelection
        identity={identity}
        albumFolder={albumFolder}
        onDeselectIdentity={onDeselectIdentity}
        onComplete={() => {
          onRequestClose();
          onComplete?.();
        }}
        onSkip={() => {
          if (albumFolderRequiresTagsToOnboard(albumFolder)) {
            onDeselectIdentity(identity);
            onRequestClose();
            return;
          }

          onRequestClose();
          onComplete?.();
        }}
        onSkipText={
          albumFolderRequiresTagsToOnboard(albumFolder)
            ? 'Choose someone else'
            : "I'm not sure - Skip"
        }
      />
    </div>
  );
};

IdentityTagSelectionModal.propTypes = propTypes;

export default withModal(
  compose(
    mapProps(({ ...props }) => ({
      ...props,
      identityId: props.identity?.id,
      albumFolderId: props.albumFolder?.id,
    })),
    // Pre-load this so that a new identity gets
    // loaded properly even if the user ends up clicking
    // no sports.
    getIdentityAlbumFolderTags,
  )(IdentityTagSelectionModal),
  {
    name: IDENTITY_TAG_SELECTION_MODAL,
    hideHeader: true,
    size: 'sm',
    type: 'full',
    shouldCloseOnOverlayClick: false,
  },
);
